import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import { Button } from '../components/ui/Button';
import { H1, Text, H2 } from '../components/ui/Typography';
import { Card } from '../components/ui/Card';
import SafeIcon from '../common/SafeIcon';

const fadeIn = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const BlendedFamily = () => {
  const { lang } = useParams();
  const isEn = lang === 'en';

  const content = {
    badge: isEn ? 'Insightful preparation for your notary appointment' : 'Moderne Familieconstructies',
    title: isEn ? 'Blended Families & Inheritance' : 'Samengesteld gezin en nalatenschap',
    subtitle: isEn 
      ? 'Protect your partner without unintentionally disadvantaging your children. Discover with a free scan which legal and strategic considerations apply to your family situation.'
      : 'Bescherm uw partner, zonder uw eigen kinderen uit het oog te verliezen. Ontdek met een gratis scan welke keuzes en aandachtspunten in uw situatie spelen.',
    cta: isEn ? 'Start Free Scan' : 'Start gratis scan',
    footer: isEn ? 'Insightful preparation for your notary appointment' : 'Discrete voorbereiding op de notaris',
    section1Title: isEn ? 'Why the standard legal arrangement often doesn’t fit' : 'Waarom de standaard wettelijke regeling vaak niet past',
    section1Text: isEn
      ? 'Inheritance law was designed for the traditional family structure. In blended families, automatic inheritance rules can unintentionally create conflicts between partners, children from previous relationships, and other heirs. Without explicit choices, outcomes may differ from what you actually intended.'
      : 'De wet is geschreven voor het \'traditionele\' gezin. Bij een samengesteld gezin sluit de automatische verdeling zelden aan op uw werkelijke wensen. Zonder expliciete keuzes kunnen stiefkinderen, eigen kinderen en partners in situaties terechtkomen die u nooit zo bedoeld heeft.',
    risks: [
      { 
        title: isEn ? 'Conflicting interests' : 'Tegenstrijdige belangen', 
        desc: isEn ? 'Partners and children may have different interests, which can lead to legal uncertainty or family tension.' : 'Partner en kinderen hebben niet altijd dezelfde belangen, wat kan leiden tot onbedoelde juridische frictie.', 
        icon: 'AlertTriangle' 
      },
      { 
        title: isEn ? 'Standard distribution may not fit' : 'Standaardverdeling matcht niet', 
        desc: isEn ? 'Default inheritance rules often do not reflect the complexity of your specific family structure and wishes.' : 'De standaard wettelijke verdeling sluit vaak niet aan op uw specifieke gezinssituatie en wensen.', 
        icon: 'FileText' 
      },
      { 
        title: isEn ? 'Family tensions' : 'Familie-spanningen', 
        desc: isEn ? 'Unclear arrangements can later lead to disputes or emotional conflicts within the family.' : 'Onduidelijke keuzes kunnen later leiden tot spanning of discussie binnen de familie over de verdeling.', 
        icon: 'MessageSquare' 
      }
    ],
    receiveTitle: isEn ? 'What you receive' : 'Wat u ontvangt',
    receiveText: isEn 
      ? 'After the free scan, you receive a strategic overview with the most important points regarding partner protection, children’s inheritance rights, and preparation for your notary appointment.'
      : 'Na de gratis scan ontvangt u een strategisch dossier met de belangrijkste aandachtspunten voor partnerbescherming, kinderbescherming en de voorbereiding op het gesprek met uw notaris.',
    points: isEn ? [
      'Insight into possible bottlenecks within your family structure',
      'Key considerations for protecting your partner and children',
      'Concrete follow-up steps for discussions with your notary'
    ] : [
      'Inzicht in mogelijke knelpunten binnen uw gezinssituatie',
      'Aandachtspunten voor bescherming van partner en kinderen',
      'Concrete vervolgstappen voor het gesprek met uw notaris'
    ],
    stepsTitle: isEn ? 'A clear plan in 3 steps' : 'In 3 stappen naar een helder plan',
    stepsText: isEn 
      ? 'We translate your family situation into a concrete preparatory dossier.'
      : 'Wij vertalen uw gezinssituatie naar een concreet voorbereidend dossier.',
    steps: [
      { step: isEn ? '1. Intake' : '1. Intake', desc: isEn ? 'Answer questions about your family situation and wishes through our secure interface.' : 'Beantwoord vragen over uw gezin en wensen via onze discrete interface.' },
      { step: isEn ? '2. Analysis' : '2. Analyse', desc: isEn ? 'Our system identifies the key legal risks and opportunities within your situation.' : 'Ons systeem identificeert direct de risico\'s en kansen voor uw situatie.' },
      { step: isEn ? '3. Overview' : '3. Dossier', desc: isEn ? 'Receive a structured summary with concrete points to discuss with your notary.' : 'Ontvang een dossier met concrete punten voor uw gesprek bij de notaris.' }
    ],
    finalTitle: isEn ? 'Gain clarity before important decisions are made' : 'Krijg helderheid voordat keuzes vastliggen',
    finalText: isEn 
      ? 'A blended family often requires more than a standard legal solution. Start the free scan and discover which steps best fit your situation.'
      : 'Een samengesteld gezin vraagt om meer dan een standaardoplossing. Start de gratis scan en ontdek welke stappen passen bij uw situatie.'
  };

  return (
    <div className="w-full">
      <section className="relative pt-12 pb-24 overflow-hidden">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-50 rounded-full blur-3xl opacity-50 -z-10"></div>
        <div className="max-w-5xl mx-auto px-6 text-center">
          <motion.div initial="hidden" animate="visible" variants={fadeIn} className="space-y-8">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-zinc-100 text-zinc-600 text-[10px] font-bold uppercase tracking-widest mb-4">
              <SafeIcon name="Users" className="text-zinc-400" />
              <span>{content.badge}</span>
            </div>
            <H1 className="text-gradient leading-tight">{content.title}</H1>
            <Text className="max-w-2xl mx-auto text-lg md:text-xl">{content.subtitle}</Text>
            <div className="flex flex-col items-center gap-6 pt-4">
              <Link to={`/${lang}/scan`}>
                <Button className="px-12 py-5 text-base shadow-xl">{content.cta}</Button>
              </Link>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em]">{content.footer}</p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-zinc-50">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <H2 className="mb-6 text-2xl md:text-3xl">{content.section1Title}</H2>
          <Text className="text-slate-600">{content.section1Text}</Text>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8">
            {content.risks.map((risk, i) => (
              <Card key={i} className="p-8 border-zinc-100 shadow-sm" hover={true}>
                <div className="w-12 h-12 bg-zinc-50 rounded-xl flex items-center justify-center mb-6 text-navy-900">
                  <SafeIcon name={risk.icon} className="text-xl" />
                </div>
                <h3 className="text-lg font-bold text-navy-900 mb-3">{risk.title}</h3>
                <Text subtle className="text-sm leading-relaxed">{risk.desc}</Text>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-zinc-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-3xl font-bold text-navy-900 mb-6">{content.receiveTitle}</h2>
              <Text className="mb-8">{content.receiveText}</Text>
              <ul className="space-y-4">
                {content.points.map((point, i) => (
                  <li key={i} className="flex items-center gap-3 text-zinc-900 font-semibold text-sm">
                    <div className="w-6 h-6 rounded-full bg-navy-900 flex items-center justify-center text-white shrink-0">
                      <SafeIcon name="Check" className="text-[10px]" />
                    </div>
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <div className="relative">
              <Card className="p-10 border-none shadow-2xl bg-white rounded-[2.5rem]">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-4">{isEn ? 'Preview Strategic Dossier' : 'Preview Strategisch Dossier'}</div>
                <div className="space-y-6">
                  <div className="h-2 w-full bg-slate-100 rounded"></div>
                  <div className="h-2 w-5/6 bg-slate-100 rounded"></div>
                  <div className="pt-4 border-t border-slate-50">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-bold">{isEn ? 'Partner Protection' : 'Partnerbescherming'}</span>
                      <span className="text-xs text-amber-600 font-bold">{isEn ? 'Attention Required' : 'Aandacht vereist'}</span>
                    </div>
                    <div className="h-1.5 w-full bg-slate-50 rounded-full overflow-hidden">
                      <div className="h-full bg-amber-500 w-1/3"></div>
                    </div>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 text-center mb-16">
          <h2 className="text-3xl font-bold text-navy-900 mb-4">{content.stepsTitle}</h2>
          <Text className="max-w-2xl mx-auto">{isEn ? 'We translate your family situation into a clear preparatory dossier.' : 'Wij vertalen uw gezinssituatie naar een concreet voorbereidend dossier.'}</Text>
        </div>
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            {content.steps.map((s, i) => (
              <div key={i} className="space-y-4">
                <div className="text-navy-900 font-bold text-sm uppercase tracking-widest">{s.step}</div>
                <Text subtle className="text-sm">{s.desc}</Text>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-navy-950 text-white text-center rounded-t-[3rem] relative overflow-hidden">
        <div className="absolute inset-0 bg-blueprint-pattern opacity-[0.03]"></div>
        <div className="max-w-3xl mx-auto px-6 relative z-10">
          <h2 className="text-3xl md:text-4xl font-semibold mb-6 tracking-tight">{content.finalTitle}</h2>
          <p className="text-slate-400 text-lg mb-10">{content.finalText}</p>
          <Link to={`/${lang}/scan`}>
            <Button variant="white" className="px-12 py-5 text-base">{content.cta}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlendedFamily;